import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate environment variables
if (!supabaseUrl || !supabaseKey) {
  console.error('Missing required environment variables:');
  if (!supabaseUrl) console.error('- VITE_SUPABASE_URL');
  if (!supabaseKey) console.error('- VITE_SUPABASE_ANON_KEY');
  throw new Error('Missing required Supabase environment variables');
}

// Create Supabase client with enhanced configuration
export const supabase = createClient<Database>(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'x-application-name': 'animatronic-database'
    }
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Enhanced error handling
supabase.handleError = (error: any) => {
  // Log error for debugging
  console.error('Supabase error:', error);
  
  // Network errors
  if (!navigator.onLine) {
    return new Error('You appear to be offline. Please check your internet connection.');
  }

  if (error.message?.includes('Failed to fetch') || error.code === 'NETWORK_ERROR') {
    return new Error('Unable to connect to the server. Please check your internet connection.');
  }

  // Rate limiting
  if (error.status === 429) {
    return new Error('Too many requests. Please wait a moment and try again.');
  }

  // Authentication errors
  if (error.status === 401 || error.code === 'UNAUTHENTICATED') {
    return new Error('Your session has expired. Please sign in again.');
  }

  // Permission errors
  if (error.status === 403 || error.code === 'UNAUTHORIZED') {
    return new Error('You do not have permission to perform this action.');
  }

  // Server errors
  if (error.status >= 500) {
    return new Error('Server error. Our team has been notified and is working on it.');
  }

  // Database errors
  if (error.code?.startsWith('23')) {
    return new Error('Database error. Please try again.');
  }

  // Default error
  return error;
};

// Enhanced retry logic with exponential backoff
supabase.handleRetry = async (operation: () => Promise<any>, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;
      
      // Don't retry on certain errors
      if (error.status === 401 || error.status === 403 || error.status === 404) {
        throw supabase.handleError(error);
      }
      
      // Exponential backoff with jitter
      const delay = Math.min(1000 * Math.pow(2, attempt) + Math.random() * 1000, 10000);
      
      console.warn(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw supabase.handleError(lastError);
};

// Add health check
supabase.checkConnection = async () => {
  try {
    const { data, error } = await supabase
      .from('animatronics')
      .select('id')
      .limit(1)
      .single();

    return !error;
  } catch {
    return false;
  }
};